import type { EpisodeDto, SeasonDto, TvShowDto } from '@bldsoft/cappie/api/wbs'
import type {
  PlaylistSeason,
  PurchaseInfo,
  PurchaseInfoContentPurchaseType,
  TvShow,
  TvShowEpisode,
  TvShowSeason,
  TvShowUpdated,
} from '@setplex/tria-api'
import type { IFormatSeasonPlaylistParams } from './index.h'

import {
  MediaTypes,
  PurchaseInfoContentPurchaseType as PurchaseInfoContentPurchaseTypeEnum,
} from '@setplex/tria-api'
import {
  getBackgroundImage,
  getHorizontalImage,
  getVerticalImage,
} from '../../utils/images'
import {
  formatDuration,
  formatResolution,
  humanizeLengthWithoutSeconds,
} from '../../utils/media'
import { formatPriceSettings } from '../../utils/payment'

// TODO fix sizes later as they in test mode before show
export const formatShow = ({
  landscapeImageUrl,
  portraitImageUrl,
  backgroundImageUrl,
  isTrailerExists,
  free,
  priceSettings,
  purchaseInfo,
  resolution,
  labels,
  ...rest
}: Omit<TvShowDto, 'contentType'>): TvShow => {
  return {
    ...rest,
    id: rest.id!,
    landscapeImageUrl: getHorizontalImage(landscapeImageUrl),
    portraitImageUrl: getBackgroundImage(portraitImageUrl),
    backgroundImageUrl: getBackgroundImage(backgroundImageUrl),
    trailerPresent: Boolean(isTrailerExists),
    mediaType: MediaTypes.TV_SHOW,
    free: free === undefined ? true : free, // Set field free is true for Nora 1.46 version, where there is no field free
    priceSettings: formatPriceSettings(priceSettings),
    purchaseInfo: purchaseInfo as PurchaseInfo | undefined,
    labels: labels as string[] | undefined,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
    resolution: formatResolution({ resolution }),
    contentType: MediaTypes.TV_SHOW,
  }
}

export const formatSeason = ({
  landscapeImageUrl,
  portraitImageUrl,
  backgroundImageUrl,
  free,
  seasonPriceSettings,
  tvShowPriceSettings,
  purchaseInfo,
  year,
  labels,
  ...rest
}: SeasonDto): TvShowSeason => {
  return {
    id: rest.id!,
    landscapeImageUrl: getHorizontalImage(landscapeImageUrl),
    portraitImageUrl: getVerticalImage(portraitImageUrl),
    backgroundImageUrl: getBackgroundImage(backgroundImageUrl),
    free: free === undefined ? true : free, // Set field free is true for Nora 1.46 version, where there is no field free
    seasonPriceSettings: formatPriceSettings(seasonPriceSettings),
    tvShowPriceSettings: formatPriceSettings(tvShowPriceSettings),
    purchaseInfo: (purchaseInfo as PurchaseInfo) || null,
    rented:
      purchaseInfo?.contentPurchaseType ===
      PurchaseInfoContentPurchaseTypeEnum.Rented,
    year: year !== undefined ? String(year) : undefined,
    labels: labels as string[] | undefined,
    ...rest,
  }
}

function formatTvShowPurchaseType(
  type: 'BOUGHT' | 'RENTED' | 'RENTED_UNLIMITED' | undefined
): PurchaseInfoContentPurchaseType {
  if (type === 'BOUGHT') return PurchaseInfoContentPurchaseTypeEnum.Bought
  if (type === 'RENTED_UNLIMITED')
    return PurchaseInfoContentPurchaseTypeEnum.RentedUnlimited

  return PurchaseInfoContentPurchaseTypeEnum.Rented
}

export const formatEpisode = ({
  landscapeImageUrl,
  portraitImageUrl,
  backgroundImageUrl,
  length,
  free,
  episodePriceSettings,
  seasonPriceSettings,
  tvShowPriceSettings,
  purchaseInfo,
  labels,
  ...rest
}: EpisodeDto): TvShowEpisode => {
  const showPurchaseInfo = purchaseInfo
    ? {
        ...purchaseInfo,
        contentPurchaseType: formatTvShowPurchaseType(
          purchaseInfo?.contentPurchaseType
        ),
      }
    : undefined

  return {
    landscapeImageUrl: getHorizontalImage(landscapeImageUrl),
    portraitImageUrl: getVerticalImage(portraitImageUrl),
    backgroundImageUrl: getBackgroundImage(backgroundImageUrl),
    length: humanizeLengthWithoutSeconds({ length }),
    free: free === undefined ? true : free, // Set field free is true for Nora 1.46 version, where there is no field free
    episodePriceSettings: formatPriceSettings(episodePriceSettings),
    seasonPriceSettings: formatPriceSettings(seasonPriceSettings),
    tvShowPriceSettings: formatPriceSettings(tvShowPriceSettings),
    duration: formatDuration({ length }),
    purchaseInfo: showPurchaseInfo,
    labels: labels as string[] | undefined,
    ...rest,
  }
}

export const formatTvShowUpdatedOnly = ({
  id,
  updated,
}: TvShowDto): TvShowUpdated => {
  return {
    id,
    updated,
  }
}

export const formatSeasonPlaylist = ({
  season,
  episodes,
  episodesAmount,
}: IFormatSeasonPlaylistParams): PlaylistSeason => {
  return {
    ...season,
    episodesAmount: episodesAmount || 0,
    episodes: episodes || [],
  }
}
